import { rejectDeprecatedTestTrendyolBrandId } from '../../utils/trendyolBrandIds';

const toText = (value) => String(value ?? '').trim();

const toPayloadId = (value) => {
  const text = toText(value);
  if (!text) return null;
  const numberValue = Number(text);
  return Number.isFinite(numberValue) ? numberValue : text;
};

const firstText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return '';
};

export const TRENDYOL_PRODUCT_FIELDS = [
  'ty_title',
  'ty_barcode',
  'ty_stockCode',
  'ty_productMainId',
  'ty_brandId',
  'ty_brandName',
  'ty_categoryId',
  'ty_categoryName',
  'ty_categoryPath',
  'ty_aciklama',
  'ty_vatRate',
  'ty_dimensionalWeight',
  'ty_cargoCompanyId',
  'ty_deliveryDuration',
  'ty_gorselURL1',
  'ty_gorselURL2',
  'ty_gorselURL3',
  'ty_gorselURL4',
  'ty_gorselURL5'
];

/** Tekli Trendyol formu ile aynı (urunekle / TrendyolProductInfoForm seçenekleri) */
export const TRENDYOL_VAT_RATE_OPTIONS = ['0', '1', '10', '20'];
export const TRENDYOL_DELIVERY_DURATION_OPTIONS = ['1', '2', '3', '4', '5'];
export const TRENDYOL_CARGO_COMPANY_SELECT_IDS = ['4', '8', '7', '9', '19', '694', '38'];
export const TRENDYOL_CARGO_COMPANY_LABEL_BY_ID = {
  '4': 'Yurtiçi Kargo',
  '8': 'MNG Kargo',
  '7': 'Aras Kargo',
  '9': 'Sürat Kargo',
  '19': 'PTT Kargo',
  '694': 'Hepsijet',
  '38': 'Kolay Gelsin'
};

export function buildTrendyolProductDefaults(source = {}) {
  const barcode = toText(source.ty_barcode || source.barkod);
  const stockCode = toText(source.ty_stockCode || source.stokKodu || barcode);
  const productMainId = toText(source.ty_productMainId || barcode);
  return {
    ty_title: toText(source.ty_title || source.isim || source.urunAdi),
    ty_barcode: barcode,
    ty_stockCode: stockCode,
    ty_productMainId: productMainId,
    ty_brandId: firstText(
      rejectDeprecatedTestTrendyolBrandId(source.ty_brandId),
      rejectDeprecatedTestTrendyolBrandId(source.defaultBrandId)
    ),
    ty_brandName: toText(source.ty_brandName || 'UKBA GOLD'),
    ty_categoryId: toText(source.ty_categoryId),
    ty_categoryName: toText(source.ty_categoryName),
    ty_categoryPath: toText(source.ty_categoryPath || source.ty_categoryName),
    ty_aciklama: toText(source.ty_aciklama || source.aciklama),
    ty_vatRate: firstText(source.ty_vatRate, source.defaultVatRate),
    ty_dimensionalWeight: firstText(source.ty_dimensionalWeight, source.desi, source.defaultDimensionalWeight),
    ty_cargoCompanyId: firstText(source.ty_cargoCompanyId, source.defaultCargoCompanyId, '4'),
    ty_deliveryDuration: firstText(source.ty_deliveryDuration, source.defaultDeliveryDuration)
  };
}

export function normalizeTrendyolProductInfo(value = {}, source = {}) {
  const defaults = buildTrendyolProductDefaults(source);
  const normalized = TRENDYOL_PRODUCT_FIELDS.reduce((acc, field) => {
    const fromValue =
      field === 'ty_brandId'
        ? rejectDeprecatedTestTrendyolBrandId(toText(value[field]))
        : toText(value[field]);
    acc[field] = fromValue || defaults[field] || '';
    return acc;
  }, {});
  normalized.ty_attributes = normalizeTrendyolAttributes(value.ty_attributes ?? source.ty_attributes);
  return normalized;
}

export function normalizeTrendyolAttributes(value = []) {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => ({
      attributeId: toPayloadId(item?.attributeId),
      attributeName: toText(item?.attributeName),
      attributeValueId: toPayloadId(item?.attributeValueId),
      attributeValueName: toText(item?.attributeValueName),
      customAttributeValue: toText(item?.customAttributeValue)
    }))
    .filter((item) => item.attributeId != null);
}

export function buildTrendyolProductFirestorePatch(value = {}, source = {}) {
  const normalized = normalizeTrendyolProductInfo(value, source);
  const patch = TRENDYOL_PRODUCT_FIELDS.reduce((acc, field) => {
    const v = normalized[field];
    /** `|| null` ile '0' (KDV vb.) yanlışlıkla null olurdu */
    acc[field] = v === '' || v == null ? null : v;
    return acc;
  }, {});
  patch.ty_attributes = normalizeTrendyolAttributes(value.ty_attributes ?? source.ty_attributes);
  return patch;
}

export function getTrendyolProductMissingFields(value = {}, source = {}) {
  const normalized = normalizeTrendyolProductInfo(value, source);
  const checks = [
    ['ty_title', 'Trendyol ürün adı'],
    ['ty_barcode', 'Barkod'],
    ['ty_stockCode', 'Stok kodu'],
    ['ty_productMainId', 'Ana ürün kodu'],
    ['ty_brandId', 'Marka ID'],
    ['ty_categoryId', 'Trendyol kategori ID'],
    ['ty_aciklama', 'Trendyol açıklaması'],
    ['ty_vatRate', 'KDV oranı'],
    ['ty_dimensionalWeight', 'Desi'],
    ['ty_cargoCompanyId', 'Kargo firması ID']
  ];
  return checks.filter(([field]) => !normalized[field]).map(([, label]) => label);
}

/** Sadece Trendyol kanal görselleri (web görselleri sayılmaz). */
const hasAnyTrendyolChannelImages = (source = {}) =>
  [1, 2, 3, 4, 5].some((index) => {
    const url = toText(source?.[`ty_gorselURL${index}`]);
    const file = source?.[`ty_gorsel${index}`];
    return Boolean(url || file);
  });

const hasAnyProductImage = (imageSource = {}, options = {}) => {
  if (hasAnyTrendyolChannelImages(imageSource)) return true;
  // Üst sayfa: seçilen dosya / blob önizleme henüz ty_gorselURL alanına yansımamış olabilir
  if (options.hasProductImage === true) return true;
  if (options.hasProductImage === false) return false;
  return false;
};

export function validateTrendyolProductReadiness(value = {}, source = {}, options = {}) {
  const normalized = normalizeTrendyolProductInfo(value, source);
  const selectedAttributes = normalizeTrendyolAttributes(normalized.ty_attributes ?? source.ty_attributes);
  const requiredAttributes = Array.isArray(options.requiredAttributes) ? options.requiredAttributes : [];
  const checks = [
    ['ty_title', 'Trendyol ürün adı'],
    ['ty_barcode', 'Barkod'],
    ['ty_stockCode', 'Stok kodu'],
    ['ty_productMainId', 'Ana ürün kodu'],
    ['ty_brandId', 'Marka ID'],
    ['ty_categoryId', 'Trendyol kategori'],
    ['ty_vatRate', 'KDV oranı'],
    ['ty_dimensionalWeight', 'Desi'],
    ['ty_cargoCompanyId', 'Kargo firması ID'],
    ['ty_aciklama', 'Trendyol açıklaması']
  ];

  const missingFields = checks
    .filter(([field]) => !toText(normalized[field]))
    .map(([, label]) => ({ type: 'field', label }));

  requiredAttributes.forEach((attribute) => {
    const attrId = toText(attribute?.attributeId);
    if (!attrId) return;
    const selected = selectedAttributes.find((item) => toText(item.attributeId) === attrId);
    const complete = attribute?.allowCustom
      ? Boolean(toText(selected?.customAttributeValue))
      : Boolean(toText(selected?.attributeValueId));
    if (!complete) {
      missingFields.push({
        type: 'attribute',
        label: `Özellik: ${toText(attribute?.attributeName) || attrId}`,
        attributeId: attrId
      });
    }
  });

  /** Form alanları + üst state (Firestore / dosya seçimleri) birlikte; TY URL’ler çoğu zaman value’da gelir */
  const imageSource = { ...source, ...normalized };
  if (!hasAnyProductImage(imageSource, options)) {
    missingFields.push({
      type: 'image',
      label: 'Trendyol görselleri (en az 1 ty_gorselURL veya Trendyol görsel yüklemesi)'
    });
  }

  return {
    ready: missingFields.length === 0,
    missingFields,
    missingCount: missingFields.length,
    selectedAttributeCount: selectedAttributes.length,
    requiredAttributeCount: requiredAttributes.length
  };
}

export function hasAnyTrendyolProductInfo(value = {}) {
  return (
    TRENDYOL_PRODUCT_FIELDS.some((field) =>
      field === 'ty_brandId'
        ? Boolean(rejectDeprecatedTestTrendyolBrandId(toText(value[field])))
        : Boolean(toText(value[field]))
    ) || normalizeTrendyolAttributes(value.ty_attributes).length > 0
  );
}
